import { AssetSpec, ParticleEmitterAssetSpec, PixiFactory, PointLike, SpriteAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { View } from "@westpierstudio/luma/mvc";
import { Resettable } from "@westpierstudio/luma/utils/reset";
import { GameServices } from "game/GameServices";
import { Emitter } from "pixi-particles";
import { Graphics, Sprite } from "pixi.js";
import { Reel } from "./reels/Reel";
import { ReelsLayout } from "./reels/ReelsModel";
import { string2hex } from "@pixi/utils";

interface ParticleLocationSpec extends AssetSpec {
    type: "width" | "height"
}


interface AnticipationSpec {
    lineWidth: number
    lineRadius: number
    lineColour: string
    linePadding: number
    anticipationParticlesSparkWidth: ParticleEmitterAssetSpec
    anticipationParticlesSparkHeight: ParticleEmitterAssetSpec
    anticipationParticlesBorderWidth: ParticleEmitterAssetSpec
    anticipationParticlesBorderHeight: ParticleEmitterAssetSpec
    anticipationParticlesLargeWidth: ParticleEmitterAssetSpec
    anticipationParticlesLargeHeight: ParticleEmitterAssetSpec
    anticipationParticleBorderLocations: ParticleLocationSpec[]
    anticipationParticleSparkLocations: ParticleLocationSpec[]
    anticipationParticleLargeLocations: ParticleLocationSpec[]
    anticipationOffset: PointLike
}


export class AnticipationView extends View implements Resettable {
    private readonly spec: AnticipationSpec;
    private readonly particles: Emitter[] = [];
    private readonly offsetX: number;
    private readonly offsetY: number;


    public get particleCount(): number {
        return this.particles.reduce((val, emitter) => { return emitter.particleCount + val; }, 0);
    }


    constructor(protected override readonly services: GameServices) {
        super(services);
        const { factory, resources } = this.services;

        this.spec = resources.getSpec(`anticipation`) as AnticipationSpec;
        this.offsetX = this.spec.anticipationOffset.x ?? 0;
        this.offsetY = this.spec.anticipationOffset.y ?? 0;
        this.position.y = this.offsetY;

        this.createAnticiptionGraphic();
        this.setupParticles(factory);

        this.hide();
    }

    createAnticiptionGraphic() {
        const { resources } = this.services;

        const reelSpec = resources.getSpec(`reels`) as ReelsLayout;
        const { symbolSize } = reelSpec;
        const { lineWidth, lineRadius, lineColour, linePadding } = this.spec;
        const width = symbolSize.width - linePadding;
        const height = symbolSize.height;
        const mheight = height * 4 - linePadding;
        const gwidth = width + lineWidth;
        const gheight = symbolSize.height * 4 + lineWidth - linePadding;

        const graphic = new Graphics();
        graphic.beginFill(string2hex(lineColour), 1);
        graphic.lineStyle({ color: 0 });

        graphic.drawRoundedRect(-gwidth / 2, -gheight / 2, gwidth, gheight, lineRadius);
        graphic.endFill();

        const mask = new Graphics();
        mask.beginFill(0xFFFFFF, 1);
        mask.drawRect(-width / 2, -mheight, -lineWidth, mheight * 2);
        mask.drawRect(width / 2, -mheight, lineWidth, mheight * 2);
        mask.drawRect(-width, -mheight / 2, width * 2, -lineWidth);
        mask.drawRect(-width, mheight / 2, width * 2, lineWidth);
        mask.endFill();
        mask.isMask = true;
        graphic.mask = mask;

        this.addChild(graphic);
        this.addChild(mask);
    }

    private setupParticles(factory: PixiFactory) {
        for (let index = 0; index < this.spec.anticipationParticleBorderLocations.length; index++) {
            const location = this.spec.anticipationParticleBorderLocations[index];
            const particleAsset = location.type === `width` ? this.spec.anticipationParticlesBorderWidth : this.spec.anticipationParticlesBorderHeight;
            const emitter = factory.createParticleEmitter(particleAsset, this);
            factory.applySpec(emitter.parent, location);
            this.particles.push(emitter);
        }

        for (let index = 0; index < this.spec.anticipationParticleSparkLocations.length; index++) {
            const location = this.spec.anticipationParticleSparkLocations[index];
            const particleAsset = location.type === `width` ? this.spec.anticipationParticlesSparkWidth : this.spec.anticipationParticlesSparkHeight;
            const emitter = factory.createParticleEmitter(particleAsset, this);
            factory.applySpec(emitter.parent, location);
            this.particles.push(emitter);
        }

        for (let index = 0; index < this.spec.anticipationParticleLargeLocations.length; index++) {
            const location = this.spec.anticipationParticleSparkLocations[index];
            const particleAsset = location.type === `width` ? this.spec.anticipationParticlesLargeWidth : this.spec.anticipationParticlesLargeHeight;
            const emitter = factory.createParticleEmitter(particleAsset, this);
            factory.applySpec(emitter.parent, location);
            this.particles.push(emitter);
        }

        this.stopParticles();
    }

    show(reel: Reel) {
        this.visible = true;
        this.position.x = reel.position.x;
        this.position.x += this.offsetX;
        this.startParticles();
    }

    hide() {
        this.visible = false;
        this.stopParticles();
    }

    private stopParticles() {
        for (const particle of this.particles) {
            particle.emit = false;
            particle.cleanup();
            particle.resetPositionTracking();
        }
    }

    private startParticles() {
        for (const particle of this.particles) {
            particle.emit = true;
            particle.update(100);
        }
    }

    override reset(): void {
        this.hide();
    }
}